import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiClient } from "@/lib/apiClient";
import { apiPath } from "@/lib/apiPath";
import type { AppointmentsResponse } from "../types";

// Use a clean axios instance without the global interceptors
// that would override our explicit Authorization header
const clientApi = axios.create();

export function useClientAppointments(accessToken: string | null) {
    return useQuery({
        queryKey: ["client", "appointments"],
        queryFn: async (): Promise<AppointmentsResponse> => {
            // Get the base URL from the configured apiClient
            const baseURL = apiClient.defaults.baseURL || "";
            const url = baseURL + apiPath("/public/client/appointments");
            const { data } = await clientApi.get(url, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            return data;
        },
        enabled: !!accessToken,
        staleTime: 30_000,
    });
}

export function useCancelAppointment(accessToken: string | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ["client", "cancel-appointment"],
        mutationFn: async (recordId: number) => {
            // Get the base URL from the configured apiClient
            const baseURL = apiClient.defaults.baseURL || "";
            const url = baseURL + apiPath(`/public/client/appointments/${recordId}`);
            const { data } = await clientApi.delete(url, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["client", "appointments"] });
        },
    });
}
